import { ReactNode, useMemo, useCallback } from "react";

import {
  Box,
  BoxProps,
  Checkbox,
  ChevronDownIcon,
  ChevronRightIcon,
  IconButton,
  Tooltip,
} from "@hightouch/htui";

import { Cell } from "./cells";
import { RowClickHandler } from "./table";
import { Skeleton, useSkeletonContext } from "src/components/skeletons";
import { getRowAnimationDelay, SKIP_HIGHLIGHT_ATTRIBUTE } from "./utils";

/** No token carries an alpha form of the oklch palette, so `color-mix` — same
 *  reason as `message-composer/dynamic-content/iframe-interaction.ts`. */
const HIGHLIGHT_TINT =
  "color-mix(in srgb, var(--chakra-colors-primary-base) 12%, transparent)";

export type RowProps<RowData extends Record<string, unknown>> = {
  children: ReactNode;
  selected?: boolean;
  /** Pointed at from outside the table. Not selection: it ticks no checkbox. */
  isHighlighted?: boolean;
  highlightColor?: BoxProps["bg"];
  disabled?: boolean | string;
  selectable?: boolean | string;
  clickable?: boolean;
  row: RowData;
  onClick?: RowClickHandler<RowData>;
  onSelect?: ((value: any) => unknown) | undefined;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  sortable?: boolean;
  primaryKey: any;
  isExpanded?: boolean;
  setIsExpanded?: (isExpanded: boolean) => void;
  rowIndex: number;
  ariaRowIndex?: number;
};

export function Row<RowData extends Record<string, unknown>>({
  selected,
  isHighlighted,
  highlightColor,
  children,
  disabled,
  selectable,
  clickable,
  onSelect,
  row,
  onClick,
  onMouseEnter,
  onMouseLeave,
  sortable,
  primaryKey,
  isExpanded = false,
  setIsExpanded,
  rowIndex,
  ariaRowIndex,
}: RowProps<RowData>) {
  const { isLoading } = useSkeletonContext();

  const handleClick = useCallback(
    (event: any) => {
      if (onClick && !window?.getSelection()?.toString()) {
        onClick(row, event);
      }
    },
    [onClick, row],
  );

  const handleSelect = useCallback(() => {
    if (typeof onSelect === "function") {
      onSelect(row[primaryKey]);
    }
  }, [onSelect, row, primaryKey]);

  const style = useMemo(() => {
    // `:not()` carries its argument's specificity, so the narrowed selector
    // exists only where an opaque highlight needs cells to opt out.
    const cellSelector =
      highlightColor === undefined
        ? "td"
        : `td:not([${SKIP_HIGHLIGHT_ATTRIBUTE}])`;
    const opaqueHighlight =
      isHighlighted && highlightColor !== undefined
        ? highlightColor
        : undefined;
    return {
      display: "contents",
      cursor: clickable ? "pointer" : "auto",
      td: {
        opacity: disabled ? 0.7 : 1,
        bg: selected ? "primary.background" : undefined,
        borderColor: selected ? "primary.border" : undefined,
        // A layer over each cell's own background rather than a replacement, so
        // a cell that paints its own state keeps it while the row is
        // highlighted. `bg` would win instead: this rule reaches `td` through
        // the row's class, which out-specifies the cell's own. Kebab-case
        // because Chakra parses `backgroundImage` as a gradient and throws on a
        // `color-mix()` stop.
        "background-image":
          isHighlighted && highlightColor === undefined
            ? `linear-gradient(${HIGHLIGHT_TINT}, ${HIGHLIGHT_TINT})`
            : undefined,
      },
      _hover: {
        [cellSelector]: {
          // Pointing at a row outranks hovering it, whichever rule the cascade
          // picks, so the hover rule carries the highlight color itself.
          bg:
            opaqueHighlight ??
            (selected
              ? "primary.background"
              : clickable
                ? "base.lightBackground"
                : undefined),
        },
      },
      // An opaque color can't layer, so it replaces each cell's background.
      ...(opaqueHighlight === undefined
        ? {}
        : { [cellSelector]: { bg: opaqueHighlight } }),
    };
  }, [clickable, selected, disabled, isHighlighted, highlightColor]);

  const checkboxDisabled =
    Boolean(disabled) || (selectable !== undefined && selectable !== true);
  const checkboxTooltip =
    typeof selectable === "string"
      ? selectable
      : typeof disabled === "string"
        ? disabled
        : undefined;

  const checkboxInput = (
    <Checkbox
      isChecked={Boolean(selected)}
      isDisabled={checkboxDisabled}
      onChange={handleSelect}
    />
  );

  const checkbox = onSelect ? (
    <Skeleton
      isLoading={isLoading}
      animationDelay={getRowAnimationDelay(rowIndex)}
      borderRadius="md"
    >
      {checkboxTooltip && !isLoading ? (
        <Tooltip message={checkboxTooltip} placement="right">
          {checkboxInput}
        </Tooltip>
      ) : (
        checkboxInput
      )}
    </Skeleton>
  ) : null;

  return (
    <Box
      as="tr"
      className="group"
      aria-rowindex={ariaRowIndex}
      sx={style}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {setIsExpanded && (
        <Cell onClick={stopPropagation}>
          <IconButton
            icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
            aria-label={isExpanded ? "Collapse row" : "Expand row"}
            onClick={() => setIsExpanded(!isExpanded)}
            variant="tertiary"
            size="sm"
          />
        </Cell>
      )}
      {checkbox && <Cell onClick={stopPropagation}>{checkbox}</Cell>}
      {children}
      {sortable && <Cell />}
    </Box>
  );
}

const stopPropagation = (event) => {
  event.stopPropagation();
};
